import { FieldType, FormField, PathHop } from '@/features/application/types/forms';
import { FIELD_TYPE_COMPATIBILITY } from '../constants/field-type-compatibility';
import { FormStructure } from '@/features/application/types/forms';
import { CrmField } from '../types';
import { useFormBuilderStore } from '../store/form-builder.store';

/**
 * Get a list of all compatible Salesforce field types for a given form field type
 */

/**
 * Interface for representing an incompatible field mapping when primary object changes
 */
export interface IncompatibleFieldMapping {
  fieldId: string;
  fieldLabel: string;
  mappedObjectName: string;
  mappedFieldLabel: string;
  mappingType: 'formField';
}

/**
 * Detects field mappings that would become invalid if the primary object changes
 * @param currentFormStructure - The current form structure with existing mappings
 * @param newPrimaryObject - The proposed new primary object
 * @param newAllowedObjects - Set of objects allowed under the new primary object
 * @returns Array of incompatible field mappings
 */
export function detectIncompatibleMappingsForPrimaryObjectChange(
  currentFormStructure: FormStructure | null,
  newPrimaryObject: string,
  newAllowedObjects: Set<string>
): IncompatibleFieldMapping[] {
  const incompatibleMappings: IncompatibleFieldMapping[] = [];

  // Check form field mappings
  if (currentFormStructure?.pages) {
    currentFormStructure.pages.forEach((page) => {
      page.sections.forEach((section) => {
        section.fields.forEach((field) => {
          // Check if field has path-based mapping and if the path goes through disallowed objects
          if (field.path && field.path.length > 0 && field.fieldApiName) {
            // Get objects from PathHop array
            const objectsInPath = field.path.map((hop) => hop.object);

            // For path-based mappings, check if any object in the path is not allowed
            const hasDisallowedObject = objectsInPath.some(
              (objectName: string) => !newAllowedObjects.has(objectName)
            );

            if (hasDisallowedObject) {
              incompatibleMappings.push({
                fieldId: field.id,
                fieldLabel: field.label,
                mappedObjectName: objectsInPath[objectsInPath.length - 1] || 'Unknown',
                mappedFieldLabel: field.fieldLabel || field.fieldApiName,
                mappingType: 'formField',
              });
            }
          }
        });
      });
    });
  }

  return incompatibleMappings;
}

/**
 * Clears incompatible mappings from the form structure and bank mappings
 * @param currentFormStructure The current form structure
 * @param incompatibleMappings The list of incompatible mappings to clear
 * @returns The updated form structure and bank mappings
 */
export function clearIncompatibleMappings(
  currentFormStructure: any,
  incompatibleMappings: IncompatibleFieldMapping[]
): {
  updatedFormStructure: any;
} {
  const updatedFormStructure = currentFormStructure
    ? JSON.parse(JSON.stringify(currentFormStructure))
    : null;

  if (!updatedFormStructure) {
    return { updatedFormStructure: null };
  }

  const incompatibleFormFieldIds = new Set(
    incompatibleMappings.filter((m) => m.mappingType === 'formField').map((m) => m.fieldId)
  );

  if (incompatibleFormFieldIds.size > 0 && updatedFormStructure.pages) {
    updatedFormStructure.pages.forEach((page: any) => {
      page.sections.forEach((section: any) => {
        section.fields.forEach((field: any) => {
          if (incompatibleFormFieldIds.has(field.id)) {
            // Clear the mapping for this field
            field.fieldApiName = undefined;
            field.fieldLabel = undefined;
            field.path = [];
            field.salesforceObject = undefined;
          }
        });
      });
    });
  }

  return { updatedFormStructure };
}

/**
 * Checks for form fields that are sensitive to leading zero truncation when mapped to numeric CRM fields.
 * @param formField - The entire form field object being mapped.
 * @param crmFieldType - The type of the CRM field being mapped to.
 * @returns True if there is a risk of leading zero truncation, false otherwise.
 */
export function validateLeadingZeroRisk(formField: FormField, crmFieldType: string): boolean {
  const sensitiveSubstrings = ['federalTaxId', 'phone', 'ssn', 'zip', 'fax'];
  const numericCrmTypes = new Set(['integer', 'double', 'currency', 'int', 'decimal', 'number']);

  const fieldIdLower = formField.id.toLowerCase();
  const isSensitive = sensitiveSubstrings.some((substring) =>
    fieldIdLower.includes(substring.toLowerCase())
  );

  if (isSensitive) {
    if (numericCrmTypes.has(crmFieldType.toLowerCase())) {
      return true; // Risk detected
    }
  }

  return false; // No risk
}

/**
 * Creates a reversed mapping from CRM field types to compatible form field types
 * This allows efficient lookup of which form field types can be converted to work with a given CRM type
 */
const createReversedCompatibilityMap = (): Record<string, FieldType[]> => {
  const reversed: Record<string, FieldType[]> = {};

  // Iterate through each form field type and its compatible CRM types
  Object.entries(FIELD_TYPE_COMPATIBILITY).forEach(([formType, crmTypes]) => {
    crmTypes.forEach((crmType) => {
      if (!reversed[crmType]) {
        reversed[crmType] = [];
      }
      // Add this form type as compatible with this CRM type
      if (!reversed[crmType].includes(formType as FieldType)) {
        reversed[crmType].push(formType as FieldType);
      }
    });
  });

  return reversed;
};

// Create the reversed mapping once at module load
const REVERSED_COMPATIBILITY = createReversedCompatibilityMap();

/**
 * Gets all form field types that are compatible with the given CRM field type
 * @param crmType - The CRM field type (e.g., 'string', 'number', 'picklist')
 * @returns Array of compatible form field types
 */
export const getCompatibleFormTypes = (crmType: string): FieldType[] => {
  return REVERSED_COMPATIBILITY[crmType] || [];
};

/**
 * Checks if a form field type is compatible with a CRM field type
 * @param formType - The form field type
 * @param crmType - The CRM field type
 * @returns True if compatible, false otherwise
 */
export const isFormTypeCrmTypeCompatible = (formType: FieldType, crmType: string): boolean => {
  return FIELD_TYPE_COMPATIBILITY[formType]?.includes(crmType) || false;
};

/**
 * Validates if the form field's max length exceeds the CRM field's length limit
 * @param formField - The form field to validate
 * @param crmField - The CRM field to validate against
 * @returns Object with length details if validation fails, null if validation passes
 */
export function validateMaxLength(
  formField: FormField,
  crmField: CrmField
): { formFieldMaxLength: number; crmFieldLength: number } | null {
  // Only validate for fields that have both maxLength values
  if (
    typeof formField.formFieldMaxLength === 'number' &&
    formField.formFieldMaxLength > 0 &&
    typeof crmField.length === 'number' &&
    crmField.length > 0
  ) {
    // Check if form field max length exceeds CRM field length
    if (formField.formFieldMaxLength > crmField.length) {
      return {
        formFieldMaxLength: formField.formFieldMaxLength,
        crmFieldLength: crmField.length,
      };
    }
  }
  return null;
}

/**
 * Creates a field mapping update object for CRM field mapping
 * @param field - The current form field being mapped
 * @param selectedCrmField - The CRM field to map to
 * @param newFieldType - The new field type to convert to
 * @param path - The navigation path to the target object
 * @returns Partial field update object to apply to the form field
 */
export function createFieldMappingUpdate(
  field: any,
  selectedCrmField: any,
  newFieldType: FieldType
): Partial<any> {
  const update: Partial<any> = {
    fieldApiName: selectedCrmField.id,
    fieldLabel: selectedCrmField.label,
    type: newFieldType,
    // Copy all relevant CRM metadata
    crmFieldType: selectedCrmField.type,
    crmMaxLength: selectedCrmField.length || null,
    crmPrecision: selectedCrmField.precision || null,
    crmScale: selectedCrmField.scale || null,
    crmPicklistValues: selectedCrmField.picklistValues || null,
  };

  // Business logic: If the type is changing, reset type-specific properties
  if (field.type !== newFieldType) {
    update.placeholder = undefined;
    update.formFieldMaxLength = undefined;
    update.crmMaxLength = undefined;
  }

  // If we're mapping to a CRM picklist, always seed the UI with CRM-provided values.
  // This prevents address fields from drifting to hardcoded option value formats.
  if (selectedCrmField.picklistValues && selectedCrmField.picklistValues.length > 0) {
    update.currentPicklistOptions = selectedCrmField.picklistValues;
  }

  return update;
}

/**
 * Determines if a locator is required when the mapping path ends with a child
 * relationship and no locator is provided.
 *


/**
 * Validates that all required CRM fields are present in the mapping
 * Only enforces this check when the primary object is 'Lead', since that's the only object we create from scratch
 * @param allCrmFields - The complete list of fields for the CRM object
 * @param allMappedFields - All form fields that have fieldApiName mappings
 * @param primaryObject - The primary CRM object name
 * @returns Array of error messages for missing required fields
 */
const validateRequiredFields = (
  allCrmFields: CrmField[],
  allMappedFields: FormField[],
  primaryObject: string
): string[] => {
  const errors: string[] = [];

  if (!allCrmFields || !Array.isArray(allCrmFields)) {
    return errors;
  }

  // Only enforce required field validation for Lead objects, since we only create Leads from scratch
  // For other objects, we only update existing records, so crmRequiredOnCreate doesn't apply
  // This might change in the future if we add other objects that we create from scratch
  if (primaryObject !== 'Lead') {
    return errors;
  }

  const mappedFieldApiNames = new Set(
    allMappedFields.map((field) => field.fieldApiName).filter(Boolean)
  );

  // Find all fields that are marked as required on creation from the master list
  const requiredCrmFields = allCrmFields.filter((field) => field.crmRequiredOnCreate);

  // Check if each required field from the master list is in the set of mapped fields
  for (const requiredField of requiredCrmFields) {
    if (!mappedFieldApiNames.has(requiredField.id)) {
      errors.push(
        `Required CRM field "${requiredField.label}" (${requiredField.id}) is not mapped to any form field.`
      );
    }
  }

  return errors;
};

/**
 * Validates dependent picklist relationships - ensuring controlling fields are mapped
 * @param allMappedFields - All fields with fieldApiName
 * @param allCrmFields - Complete list of CRM fields to check for dependent relationships
 * @returns Array of error messages
 */
const validateDependentPicklists = (
  allMappedFields: FormField[],
  allCrmFields: CrmField[]
): string[] => {
  const errors: string[] = [];
  const mappedFieldApiNames = new Set(
    allMappedFields.map((field) => field.fieldApiName).filter(Boolean)
  );

  for (const mappedField of allMappedFields) {
    if (!mappedField.fieldApiName) continue;

    // Find the corresponding CRM field to check for dependent picklist properties
    const correspondingCrmField = allCrmFields.find(
      (field) => field.id === mappedField.fieldApiName
    );

    if (
      correspondingCrmField &&
      correspondingCrmField.dependentPicklist &&
      correspondingCrmField.controllingField
    ) {
      const isControllerMapped = mappedFieldApiNames.has(correspondingCrmField.controllingField);
      if (!isControllerMapped) {
        errors.push(
          `Dependent field "${mappedField.label}" is mapped, but its controlling field "${correspondingCrmField.controllingField}" is not. Please map the controlling field.`
        );
      }
    }
  }

  return errors;
};

/**
 * Validates address picklist state/country pairing when org has address picklists enabled
 * @param allMappedFields - All fields with fieldApiName
 * @param isAddressPicklistOrgEnabled - Whether address picklists are enabled
 * @returns Array of error messages
 */
const validateAddressPicklistPairing = (
  allMappedFields: FormField[],
  isAddressPicklistOrgEnabled: boolean
): string[] => {
  const errors: string[] = [];

  if (!isAddressPicklistOrgEnabled) {
    return errors;
  }

  const businessStateField = allMappedFields.find((f) => f.id === 'businessState');
  const businessCountryField = allMappedFields.find((f) => f.id === 'businessCountry');

  const stateIsMapped = businessStateField && businessStateField.fieldApiName;
  const countryIsMapped = businessCountryField && businessCountryField.fieldApiName;

  if (stateIsMapped && !countryIsMapped) {
    errors.push(
      'The Business State field is mapped, but the corresponding Business Country field is not. When State/Country picklists are enabled, both must be mapped.'
    );
    return errors;
  }

  if (stateIsMapped && countryIsMapped) {
    const stateApiName = businessStateField.fieldApiName!;
    const countryApiName = businessCountryField.fieldApiName!;

    const stateEndsWithCode = stateApiName.endsWith('StateCode');
    const countryEndsWithCode = countryApiName.endsWith('CountryCode');

    if (!stateEndsWithCode) {
      errors.push(
        `The Business State field is mapped to "${stateApiName}", which is not a valid StateCode field.`
      );
    }

    if (!countryEndsWithCode) {
      errors.push(
        `The Business Country field is mapped to "${countryApiName}", which is not a valid CountryCode field.`
      );
    }

    if (stateEndsWithCode && countryEndsWithCode) {
      const stateBase = stateApiName.replace('StateCode', '');
      const countryBase = countryApiName.replace('CountryCode', '');

      if (stateBase !== countryBase) {
        errors.push(
          `The Business State and Country fields are mapped to a non-matching pair: "${stateApiName}" and "${countryApiName}".`
        );
      }
    }
  }

  return errors;
};

/**
 * Main grid-level validation function - orchestrates all validation checks that can be performed locally
 * @param formStructure - The complete form structure
 * @param allCrmFieldsByObject - Object containing CRM fields for each object, keyed by object name
 * @param primaryObject - The primary CRM object name
 * @param isAddressPicklistOrgEnabled - Whether address picklists are enabled
 * @returns Array of error strings for any validation failures
 */
export const validateGridLevelChecks = (
  formStructure: FormStructure | null,
  allCrmFieldsByObject: Record<string, CrmField[]>,
  primaryObject: string,
  isAddressPicklistOrgEnabled: boolean = false
): string[] => {
  const errors: string[] = [];

  if (!formStructure || !primaryObject) {
    return ['Invalid input to validation function.'];
  }

  if (!formStructure.pages) {
    return ['Form structure has no pages defined.'];
  }

  // Get all mapped fields from the form structure
  const allMappedFields = formStructure.pages.flatMap((p) =>
    p.sections.flatMap((s) => s.fields.filter((field) => field.fieldApiName))
  );

  // Get CRM fields for the primary object
  const allCrmFields = allCrmFieldsByObject[primaryObject] || [];

  if (allCrmFields.length === 0) {
    errors.push('CRM fields not loaded for the selected object. Please refresh and try again.');
    return errors;
  }

  // Run all validation checks
  const requiredFieldErrors = validateRequiredFields(allCrmFields, allMappedFields, primaryObject);
  const dependentPicklistErrors = validateDependentPicklists(allMappedFields, allCrmFields);
  const addressPicklistErrors = validateAddressPicklistPairing(
    allMappedFields,
    isAddressPicklistOrgEnabled
  );

  // Combine all errors
  errors.push(...requiredFieldErrors, ...dependentPicklistErrors, ...addressPicklistErrors);

  return errors;
};

/**
 * Deep equality comparison for locator objects
 * This handles cases where JSON.stringify fails due to property order differences
 */
const areLocatorsEqual = (locator1: any, locator2: any): boolean => {
  if (locator1 === locator2) return true;
  if (!locator1 && !locator2) return true;
  if (!locator1 || !locator2) return false;

  // Compare strategy
  if (locator1.strategy !== locator2.strategy) return false;

  // Compare params object
  const params1 = locator1.params || {};
  const params2 = locator2.params || {};

  const keys1 = Object.keys(params1);
  const keys2 = Object.keys(params2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (params1[key] !== params2[key]) return false;
  }

  return true;
};

const arePathsEqual = (path1: PathHop[] | undefined, path2: PathHop[] | undefined): boolean => {
  if (!path1 && !path2) return true;
  if (!path1 || !path2 || path1.length !== path2.length) return false;

  return path1.every((hop1, index) => {
    const hop2 = path2[index];
    const locatorsEqual = areLocatorsEqual(hop1.locator, hop2.locator);
    return (
      hop1.object === hop2.object &&
      hop1.rel === hop2.rel &&
      hop1.kind === hop2.kind &&
      locatorsEqual
    );
  });
};

/**
 * Checks for duplicate CRM field mappings in the current form structure
 * @param newPath - The PathHop[] to check for duplicates
 * @param newFieldApiName - The CRM field API name to check for duplicates
 * @param excludeFieldId - Field ID to exclude from the check (typically the current field being edited)
 * @returns Array of conflicting form fields
 */
export const checkForDuplicateMapping = (
  newPath: PathHop[] | undefined,
  newFieldApiName: string,
  excludeFieldId: string
): FormField[] => {
  const { currentMappingStructure } = useFormBuilderStore.getState();

  if (!currentMappingStructure) {
    return [];
  }

  // Flatten all fields from all pages and sections
  const allFields: FormField[] = [];
  currentMappingStructure.pages.forEach((page) =>
    page.sections.forEach((section) => allFields.push(...section.fields))
  );

  const conflictingFields: FormField[] = [];

  for (const field of allFields) {
    if (field.id === excludeFieldId) continue;
    if (!field.fieldApiName) continue;

    if (field.fieldApiName === newFieldApiName) {
      const pathsEqual = arePathsEqual(field.path, newPath);

      if (pathsEqual) {
        conflictingFields.push(field);
      }
    }
  }

  return conflictingFields;
};
